import { MobileContainer } from '@/components/layout/MobileContainer';
import { getLearningContent } from '@/data/learningData';
import { ALISA_IMAGES } from '@/data/moduleData';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  BookOpen,
  Car,
  CheckCircle2,
  FileText,
  Lightbulb,
  Map,
  Package,
  Smartphone
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLangNavigate, useLocalized, useT } from '@/i18n';
import { useParams } from 'react-router-dom';

const ICON_MAP = {
  FileText,
  Car,
  Backpack: Package,
  Smartphone,
  Map,
};

function SectionImage({ src, title, color }) {
  const [hasError, setHasError] = useState(false);

  if (hasError || !src) {
    return (
      <div
        className={`w-full aspect-[16/9] rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center`}
      >
        <BookOpen size={48} className="text-white/60" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={title}
      className="w-full aspect-[16/9] rounded-2xl object-cover shadow-card"
      onError={() => setHasError(true)}
    />
  );
}

function LearningSection({ section, index }) {
  const Icon = ICON_MAP[section.icon] || FileText;

  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: 0.05 }}
      className="scroll-mt-20"
      id={section.id}
    >
      {/* Section header badge */}
      <div className="flex items-center gap-2 mb-3">
        <div
          className={`w-8 h-8 rounded-lg ${section.bgColor} flex items-center justify-center`}
        >
          <Icon size={16} className={section.iconColor} />
        </div>
        <h2 className="text-lg font-bold font-heading text-foreground">
          {section.title}
        </h2>
      </div>

      {/* Image */}
      <SectionImage
        src={section.image}
        title={section.title}
        color={section.color}
      />

      {/* Description */}
      <div className="mt-3 space-y-2">
        {section.paragraphs.map((p, i) => (
          <p key={i} className="text-sm text-muted-foreground font-body leading-relaxed">
            {p}
          </p>
        ))}
      </div>

      {/* Key points */}
      <div className="mt-4 space-y-2.5">
        {section.keyPoints.map((point, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 + i * 0.05 }}
            className="flex items-start gap-3 bg-card rounded-xl p-3 shadow-soft border border-border/50"
          >
            <div className="mt-0.5 flex-shrink-0">
              <CheckCircle2 size={16} className="text-success" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold font-heading text-foreground leading-tight">
                {point.label}
              </p>
              <p className="text-xs text-muted-foreground font-body mt-0.5">
                {point.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tip */}
      {section.tip && (
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-4 flex items-start gap-2.5 bg-amber-50 border border-amber-200/60 rounded-xl p-3"
        >
          <Lightbulb size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 font-body leading-relaxed">
            {section.tip}
          </p>
        </motion.div>
      )}
    </motion.section>
  );
}

export default function LearningScreen() {
  const { moduleId } = useParams();
  const navigate = useLangNavigate();
  const t = useT();
  const scrollRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const content = useLocalized(getLearningContent(moduleId));

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const progress = el.scrollTop / (el.scrollHeight - el.clientHeight);
    setScrollProgress(Math.min(1, progress));
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll, { passive: true });
      return () => el.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  if (!content) {
    return (
      <MobileContainer>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground font-body">
            {t('ui.learning.notFound')}
          </p>
        </div>
      </MobileContainer>
    );
  }

  const isReady = scrollProgress > 0.85;

  return (
    <MobileContainer noPadding>
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Fixed header */}
        <div className="bg-card/95 backdrop-blur-md border-b border-border z-30 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/modules')}
            className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center"
          >
            <ArrowLeft size={18} className="text-foreground" />
          </button>

          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-body">
              {t('ui.learning.label')}
            </p>
            <p className="text-sm font-bold font-heading text-foreground truncate">
              {content.title}
            </p>
          </div>

          {/* Reading progress */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground font-body">
              {Math.round(scrollProgress * 100)}%
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-0.5 bg-muted relative z-20">
          <motion.div
            className="h-full gradient-primary-btn"
            style={{ width: `${scrollProgress * 100}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>

        {/* Scrollable content */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto scrollbar-hide"
        >
          {/* Hero section */}
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 gradient-hero rounded-b-[32px]" />
            <div className="relative z-10 px-5 pt-6 pb-5">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-3"
              >
                <img
                  src={ALISA_IMAGES.icon}
                  alt={t('ui.alisa.name')}
                  className="w-12 h-12 rounded-full border-2 border-white/30 shadow-lg"
                />
                <div className="flex-1 bg-white/15 backdrop-blur-sm rounded-2xl rounded-tl-sm px-4 py-3">
                  <p className="text-sm text-primary-foreground font-body leading-relaxed">
                    {t('ui.learning.alisaHint')}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Intro */}
          <div className="px-5 py-5">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="text-xl font-extrabold font-heading text-foreground mb-1">
                {content.title}
              </h1>
              <p className="text-sm text-muted-foreground font-body mb-4">
                {content.subtitle}
              </p>
              <p className="text-sm text-foreground/80 font-body leading-relaxed">
                {content.intro}
              </p>
            </motion.div>

            {/* Table of contents */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-5 bg-card rounded-2xl shadow-card p-4 border border-border/50"
            >
              <p className="text-xs font-bold font-heading text-muted-foreground uppercase tracking-wider mb-3">
                {t('ui.learning.contents')}
              </p>
              <div className="space-y-2">
                {content.sections.map((section, i) => {
                  const Icon = ICON_MAP[section.icon] || FileText;
                  return (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="flex items-center gap-2.5 py-1.5 group"
                      onClick={(e) => {
                        e.preventDefault();
                        document
                          .getElementById(section.id)
                          ?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <div
                        className={`w-6 h-6 rounded-md ${section.bgColor} flex items-center justify-center`}
                      >
                        <Icon size={12} className={section.iconColor} />
                      </div>
                      <span className="text-sm font-body text-foreground group-hover:text-primary transition-colors">
                        {i + 1}. {section.title}
                      </span>
                    </a>
                  );
                })}
              </div>
            </motion.div>
          </div>

          {/* Sections */}
          <div className="px-5 space-y-8 pb-6">
            {content.sections.map((section, index) => (
              <LearningSection
                key={section.id}
                section={section}
                index={index}
              />
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="px-5 pb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl shadow-card p-5 border border-border/50 text-center"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <BookOpen size={22} className="text-primary" />
              </div>
              <h3 className="text-base font-bold font-heading text-foreground mb-1">
                {t('ui.learning.doneTitle')}
              </h3>
              <p className="text-xs text-muted-foreground font-body mb-4">
                {t('ui.learning.doneSubtitle')}
              </p>
              <button
                onClick={() => navigate(`/module/${moduleId}`)}
                className="w-full gradient-primary-btn text-primary-foreground font-heading font-bold text-base py-3.5 rounded-xl shadow-primary hover:shadow-primary-hover active:scale-[0.98] transition-transform duration-150 uppercase tracking-wide"
              >
                {t('ui.learning.goToTest')}
              </button>
            </motion.div>
          </div>
        </div>



        {/* Fixed bottom bar with CTA */}
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: isReady ? 0 : 100 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="bg-card/95 backdrop-blur-md border-t border-border px-4 py-3 z-30"
        >
          <button
            onClick={() => navigate(`/module/${moduleId}`)}
            className="w-full gradient-primary-btn text-primary-foreground font-heading font-bold text-sm py-3 rounded-xl shadow-primary hover:shadow-primary-hover active:scale-[0.98] transition-transform duration-150 uppercase tracking-wide"
          >
            {t('ui.learning.startTest')}
          </button>
        </motion.div>
      </div>
    </MobileContainer>
  );
}
